import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
    Box, Chip, Paper, Table, TableBody,
    TableCell, TableHead, TableRow, Typography,
} from "@mui/material";

import { financeApi, fmtKRWFull } from "../../api/financeApi";
import { ds } from "../../../../styles/designSystem";
import { formatDate } from "../../../../util/dateUtils";
import LoadingDots from "../../../../component/common/LoadingDots";
import SearchField, { searchMatch } from "../../../../component/common/SearchField";
import ComboBox from "../../../../component/common/ComboBox";
import { useFinanceLinks } from "../../components/FinanceLinkProvider";

/* 지출요청 상태 버킷 — 결재 진행 상태를 재무 관점(예정/확정/보류/반려)으로 묶는다.
   최종 결재자가 '지출확정'하면 결재가 APPROVED 가 되며 실적(완료 자금계획)으로 잡힌다. */
const BUCKET_META = {
    planned:   { label: "지출예정", color: "#2563eb",                   bg: "#eff6ff" },
    confirmed: { label: "지출확정", color: ds.finance.income,           bg: "#f0fdf4" },
    held:      { label: "보류",     color: ds.finance.scheduledPayment, bg: "#fffbeb" },
    rejected:  { label: "반려",     color: ds.finance.expense,          bg: "#fef2f2" },
};
const BUCKET_ORDER = ["planned", "confirmed", "held", "rejected"];

const bucketOf = (status) => {
    switch (status) {
        case "APPROVED": return "confirmed";
        case "HELD":     return "held";
        case "REJECTED": return "rejected";
        default:         return "planned";   // PENDING, IN_PROGRESS = 결재 진행중 = 지출예정
    }
};

// 사이드바 부서 링크와 반드시 동일한 형태여야 URL 하이라이트가 맞는다.
const deptUrl = (deptId) =>
    `/finance?section=expenserequest&tab=${encodeURIComponent("지출요청")}&dept=${deptId || "all"}`;

/**
 * 지출요청 — 결재(대금지급요청·구매품의서)를 부서별로 모아 지출 예정/확정 내역을 조회하는 화면. (조회 전용)
 * - 사이드바 '지출요청' 하위 부서 항목 또는 상단 드롭다운으로 부서를 고른다. '전체' 는 부서별로 묶어 보여준다.
 * - 행 클릭 시 원본 결재 상세로 이동한다.
 */
export default function ExpenseRequestTab({ departments = [] }) {
    const location = useLocation();
    const history  = useHistory();
    const { openApproval } = useFinanceLinks();

    const [raw, setRaw]           = useState(null);
    const [loading, setLoading]   = useState(true);
    const [bucketOn, setBucketOn] = useState(() => new Set(BUCKET_ORDER));   // 반려 포함 전체 상태 기본 표시
    const [q, setQ]               = useState("");

    // 부서 선택 — URL(?dept=) 이 단일 출처. 사이드바 부서 항목과 양방향 동기화된다.
    const deptParam   = new URLSearchParams(location.search).get("dept");
    const selectedDept = deptParam && deptParam !== "all" ? deptParam : "";

    // URL 에 dept 가 없으면 'all' 로 보정 → 사이드바 '전체' 항목 하이라이트 + 새로고침 시 선택 유지
    useEffect(() => {
        if (deptParam == null) history.replace(deptUrl("all"));
    }, [deptParam, history]);

    useEffect(() => {
        financeApi.getFinanceApprovals()
            .then(d => setRaw(Array.isArray(d) ? d : (d || [])))
            .catch(() => setRaw([]))
            .finally(() => setLoading(false));
    }, []);

    const toggleBucket = (b) => setBucketOn(prev => {
        const next = new Set(prev);
        if (next.has(b)) next.delete(b); else next.add(b);
        return next;
    });

    // 결재 응답 → 표 행. 금액·지급예정일·거래처는 타입별 form 에서 읽음.
    const rows = useMemo(() => (raw || []).map(a => {
        const fd = a.form || {};
        return {
            id: a.id,
            typeLabel: a.typeLabel,
            title: a.title,
            amount: Number(fd.amount) || 0,
            paymentDate: fd.paymentDate || "",
            vendor: fd.vendor || fd.vendorName || "",
            submitterName: a.submitterName || "",
            deptName: a.submitterDept || "미지정",
            deptId: a.submitterDeptId ?? null,
            bucket: bucketOf(a.status),
            createdAt: a.createdAt ? formatDate(a.createdAt) : "",
        };
    }), [raw]);

    const filtered = useMemo(() => rows.filter(r =>
        bucketOn.has(r.bucket)
        && (!selectedDept || String(r.deptId) === String(selectedDept))
        && searchMatch(q, [r.title, r.vendor, r.submitterName, r.deptName, r.typeLabel])
    ), [rows, bucketOn, selectedDept, q]);

    // 전체 선택 시 부서별로 묶고, 특정 부서 선택 시 단일 그룹.
    const groups = useMemo(() => {
        if (selectedDept) {
            const name = departments.find(d => String(d.deptId) === String(selectedDept))?.deptName
                || filtered[0]?.deptName || "선택 부서";
            return [{ key: selectedDept, deptName: name, rows: filtered }];
        }
        const map = new Map();
        filtered.forEach(r => {
            const k = r.deptName || "미지정";
            if (!map.has(k)) map.set(k, []);
            map.get(k).push(r);
        });
        return [...map.entries()]
            .sort((a, b) => a[0].localeCompare(b[0], "ko"))
            .map(([deptName, rs]) => ({ key: deptName, deptName, rows: rs }));
    }, [filtered, selectedDept, departments]);

    // 요약 — 지출예정 합계 · 지출확정 합계 · 총 건수
    const plannedTotal   = filtered.filter(r => r.bucket === "planned").reduce((s, r) => s + r.amount, 0);
    const confirmedTotal = filtered.filter(r => r.bucket === "confirmed").reduce((s, r) => s + r.amount, 0);

    const onDeptChange = (v) => history.push(deptUrl(v));

    return (
        <Box>
            {/* 요약 카드 */}
            <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
                <Paper sx={{ ...ds.cardPadded, flex: 1, minWidth: 190 }}>
                    <Typography sx={ds.typography.label}>지출 예정 합계 (진행중)</Typography>
                    <Typography sx={{ fontSize: 24, fontWeight: 800, color: "#2563eb", mt: 0.5 }}>
                        {fmtKRWFull(plannedTotal)}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
                        {filtered.filter(r => r.bucket === "planned").length}건
                    </Typography>
                </Paper>
                <Paper sx={{ ...ds.cardPadded, flex: 1, minWidth: 190 }}>
                    <Typography sx={ds.typography.label}>지출 확정 합계</Typography>
                    <Typography sx={{ fontSize: 24, fontWeight: 800, color: ds.finance.income, mt: 0.5 }}>
                        {fmtKRWFull(confirmedTotal)}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
                        {filtered.filter(r => r.bucket === "confirmed").length}건
                    </Typography>
                </Paper>
                <Paper sx={{ ...ds.cardPadded, flex: 1, minWidth: 190 }}>
                    <Typography sx={ds.typography.label}>표시 건수</Typography>
                    <Typography sx={{ fontSize: 24, fontWeight: 800, color: ds.finance.balance, mt: 0.5 }}>
                        {filtered.length}건
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
                        {selectedDept ? "선택 부서" : `${groups.length}개 부서`}
                    </Typography>
                </Paper>
            </Box>

            {/* 상태 필터 칩 + 부서 선택 + 검색 */}
            <Box sx={{ display: "flex", gap: 1, mb: 2, alignItems: "center", flexWrap: "wrap" }}>
                {BUCKET_ORDER.map(b => {
                    const m = BUCKET_META[b];
                    const on = bucketOn.has(b);
                    return (
                        <Chip key={b} label={m.label} size="small" onClick={() => toggleBucket(b)}
                            sx={{
                                cursor: "pointer", fontWeight: 700, fontSize: 11.5, height: 24,
                                bgcolor: on ? m.bg : "#f8fafc",
                                color: on ? m.color : "#cbd5e1",
                                border: `1px solid ${on ? `${m.color}44` : "#e2e8f0"}`,
                            }} />
                    );
                })}
                <Box sx={{ width: 160 }}>
                    <ComboBox placeholder="전체 부서" clearable={false}
                        options={[{ value: "", label: "전체 부서" },
                            ...departments.map(d => ({ value: String(d.deptId), label: d.deptName }))]}
                        value={selectedDept}
                        onChange={onDeptChange} />
                </Box>
                <Box sx={{ flex: 1 }} />
                <SearchField value={q} onChange={setQ} placeholder="내용·거래처·기안자·부서 검색" />
            </Box>

            {/* 표 — 전체는 부서별 그룹, 특정 부서는 단일 목록 */}
            <Paper sx={ds.card}>
                {loading && <LoadingDots />}
                {!loading && (
                    <Table size="small" sx={ds.tableSx}>
                        <TableHead>
                            <TableRow>
                                <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>기안일</TableCell>
                                <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>유형</TableCell>
                                <TableCell>내용</TableCell>
                                <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>거래처</TableCell>
                                <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>기안자</TableCell>
                                <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>지급예정일</TableCell>
                                <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>금액</TableCell>
                                <TableCell align="center">상태</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filtered.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 4, color: "#94a3b8" }}>
                                        해당 조건의 지출요청이 없습니다.
                                    </TableCell>
                                </TableRow>
                            ) : groups.map(g => {
                                // 부서 소계도 총합계와 동일하게 반려(rejected) 건을 제외한다 — 반려는 실제로 나갈 돈이 아니다.
                                // (총합계는 이미 반려 제외로 고쳤는데 소계만 g.rows 전체를 더해 부서별 금액이 부풀어 있었다.)
                                const subtotal = g.rows.filter(r => r.bucket !== "rejected").reduce((s, r) => s + r.amount, 0);
                                return (
                                    <React.Fragment key={g.key}>
                                        {/* 부서 그룹 헤더 — 전체 보기에서만 부서를 구분 */}
                                        {!selectedDept && (
                                            <TableRow sx={{ bgcolor: "#f1f5f9" }}>
                                                <TableCell colSpan={8} sx={{ fontWeight: 800, fontSize: 12.5, color: "#334155" }}>
                                                    {g.deptName}
                                                    <Box component="span" sx={{ ml: 1, fontWeight: 600, color: "#94a3b8" }}>
                                                        {g.rows.length}건 · {fmtKRWFull(subtotal)}
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                        {g.rows.map(r => {
                                            const m = BUCKET_META[r.bucket];
                                            return (
                                                <TableRow key={r.id} hover onClick={() => openApproval(r.id)} sx={{ cursor: "pointer" }}>
                                                    <TableCell align="center" sx={{ whiteSpace: "nowrap", fontSize: 12 }}>{r.createdAt || "—"}</TableCell>
                                                    <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                                                        <Chip label={r.typeLabel} size="small"
                                                            sx={{ height: 20, fontSize: 10.5, fontWeight: 700, bgcolor: "#eef2ff", color: "#4f46e5" }} />
                                                    </TableCell>
                                                    <TableCell align="center" sx={{ maxWidth: 280 }}>
                                                        <Typography noWrap sx={{ fontSize: 13, fontWeight: 600 }} title={r.title}>{r.title}</Typography>
                                                    </TableCell>
                                                    <TableCell align="center" sx={{ whiteSpace: "nowrap", fontSize: 12 }}>{r.vendor || "—"}</TableCell>
                                                    <TableCell align="center" sx={{ whiteSpace: "nowrap", fontSize: 12, color: "#475569" }}>{r.submitterName || "—"}</TableCell>
                                                    <TableCell align="center" sx={{ whiteSpace: "nowrap", fontSize: 12 }}>{r.paymentDate || "—"}</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 700, color: ds.finance.expense, whiteSpace: "nowrap" }}>
                                                        −{fmtKRWFull(r.amount)}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Chip label={m.label} size="small"
                                                            sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: m.bg, color: m.color }} />
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                        {/* 부서 소계 (전체 보기) */}
                                        {!selectedDept && (
                                            <TableRow>
                                                <TableCell colSpan={6} align="right" sx={{ fontWeight: 700, color: "#64748b", fontSize: 12 }}>
                                                    {g.deptName} 소계
                                                </TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 800, color: ds.finance.expense, whiteSpace: "nowrap" }}>
                                                    −{fmtKRWFull(subtotal)}
                                                </TableCell>
                                                <TableCell />
                                            </TableRow>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                            {/* 총 합계 */}
                            {filtered.length > 0 && (
                                <TableRow sx={{ bgcolor: "#f8fafc" }}>
                                    <TableCell colSpan={6} sx={{ fontWeight: 800 }}>
                                        총 합계 ({filtered.filter(r => r.bucket !== "rejected").length}건 · 반려 제외)
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 800, color: ds.finance.expense, whiteSpace: "nowrap" }}>
                                        {/* 반려 건은 실제로 나갈 돈이 아니므로 지출 합계에서 뺀다(기존엔 합산돼 총액이 부풀었다). */}
                                        −{fmtKRWFull(filtered.filter(r => r.bucket !== "rejected").reduce((s, r) => s + r.amount, 0))}
                                    </TableCell>
                                    <TableCell />
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                )}
            </Paper>
        </Box>
    );
}
